import type { components } from '@social-studio/operator-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call } from '../../lib/api';

export type Operator = components['schemas']['OperatorDto'];
export type OperatorRole = Operator['role'];
export type OperatorChanges = components['schemas']['UpdateOperatorDto'];
export type NewOperator = components['schemas']['CreateOperatorDto'];

export const OPERATOR_ROLES: readonly OperatorRole[] = ['owner', 'support', 'billing', 'viewer'];

const OPERATORS_KEY = ['operators'] as const;

export function useOperators({ enabled = true }: { readonly enabled?: boolean } = {}) {
  return useQuery({ queryKey: OPERATORS_KEY, queryFn: () => call(() => api.GET('/v1/operator/operators')), enabled });
}

/** The writes of the operators page; each refreshes the list (REQ-140-002). */
export function useOperatorWrites() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: OPERATORS_KEY });
  return {
    create: useMutation({ mutationFn: (body: NewOperator) => call(() => api.POST('/v1/operator/operators', { body })), onSuccess: refresh }),
    update: useMutation({
      mutationFn: ({ id, changes }: { id: string; changes: OperatorChanges }) =>
        call(() => api.PATCH('/v1/operator/operators/{id}', { params: { path: { id } }, body: changes })),
      onSuccess: refresh,
      onError: refresh,
    }),
    resend: useMutation({
      mutationFn: (id: string) => call(() => api.POST('/v1/operator/operators/{id}/invitations', { params: { path: { id } } })),
    }),
  };
}
